import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Divider,
  IconButton,
  Button,
  Box
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";


const LargeBondDetailsModal = ({ open, onClose, bond }) => {
  if (!bond) return null;

  const currency = (val) =>
    val != null
      ? `$${Number(val).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
      : "";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Large Bond Details
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Bond Info */}
        <Typography variant="h6" gutterBottom>
          Bond Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography><strong>Status:</strong> {bond.status}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography><strong>Amount:</strong> {currency(bond.amount)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography><strong>Requested By:</strong> {bond.requestedBy}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Defendant Info */}
        <Typography variant="h6" gutterBottom>
          Defendant Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
          <Typography>
            <strong>Name:</strong>{" "}
            {[bond.firstName, bond.middleInitial, bond.lastName].filter(Boolean).join(" ")}
            </Typography>
          </Grid>
          <Grid item xs={6}>
          <Typography>
            <strong>Date of Birth:</strong>{" "}
            {bond.dateOfBirth
                ? new Date(bond.dateOfBirth).toLocaleDateString("en-US")
                : "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography><strong>Occupation:</strong> {bond.occupation}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              <strong>Address:</strong>{" "}
              {[bond.street1, bond.street2, bond.city, bond.state, bond.zip]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography><strong>Age:</strong> {bond.age}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography><strong>Income:</strong> {currency(bond.income)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography><strong>SSN:</strong> {bond.ssn}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Indemnitor Info */}
        {Array.isArray(bond.indemnitors) && bond.indemnitors.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>
            Indemnitor(s)
          </Typography>

          {bond.indemnitors.map((ind, i) => (
            <Box key={i} sx={{ mt: 2, mb: 3, pl: 1, borderLeft: "4px solid #ccc" }}>
              <Typography variant="subtitle1" gutterBottom>
                Indemnitor #{i + 1}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography><strong>First Name:</strong> {ind.firstName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Last Name:</strong> {ind.lastName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Relationship:</strong> {ind.relationshipToDefendant}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Occupation:</strong> {ind.occupation}</Typography>
                </Grid>
                {ind.durationOfEmployment && (
                  <Grid item xs={6}>
                    <Typography><strong>Duration of Employment:</strong> {ind.durationOfEmployment}</Typography>
                  </Grid>
                )}
              </Grid>

              {ind.collaterals?.map((coll, j) => (
                <Box key={j} sx={{ mt: 2, pl: 2 }}>
                  <Typography variant="subtitle2">Collateral #{j + 1}</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography><strong>Type:</strong> {coll.collateralType}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography><strong>Amount:</strong> {coll.collateralAmount ? `$${Number(coll.collateralAmount).toLocaleString()}` : ""}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography><strong>Liens:</strong> {coll.liens}</Typography>
                    </Grid>
                    {coll.collateralType === "DE" && (
                    <Grid item xs={12}>
                      <Typography>
                        <strong>Address:</strong>{" "}
                        {[coll.collateralStreet1, coll.collateralStreet2, coll.collateralCity, coll.collateralState, coll.collateralZip]
                          .filter(Boolean)
                          .join(", ")}
                      </Typography>
                    </Grid>
                  )}
                  </Grid>
                </Box>
              ))}
            </Box>
          ))}
        </>
      )}

        {bond.status === "AP" && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Approved Bond Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography><strong>Authorization #:</strong> {bond.authorizationNumber || "N/A"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography><strong>Power Number:</strong> {bond.powerNumber || "N/A"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography><strong>Execution Amount:</strong> {currency(bond.executionAmount)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography><strong>Offense:</strong> {bond.offense || "N/A"}</Typography>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LargeBondDetailsModal;
